import { extends as _extends, objectWithoutProperties as _objectWithoutProperties, objectSpread2 as _objectSpread2, typeof as _typeof } from '../../_virtual/_rollupPluginBabelHelpers.js';
import classNames from 'classnames';
import React, { useRef, useCallback, useMemo } from 'react';
import { useListContext, ListContextProvider } from './list-context.esm.js';
import { LIST_THEME_KEY } from './types.esm.js';
import useForkRef from '../../hooks/use-fork-ref.esm.js';
import useIsomorphicEffect from '../../hooks/use-isomorphic-effect.esm.js';
import createComponent from '../../styles-system/create-component.esm.js';
import isTuiElement from '../../utils/is-theme-ui-element.esm.js';
import { getPaddingX, getPaddingY } from '../../utils/props-utils.esm.js';
import Box from '../_primitives/box/box.esm.js';
import { ButtonPrivate } from '../button/button.esm.js';
import { isEmptyObject } from '../../utils/assertion.esm.js';
import { EMPTY_OBJECT } from '../../utils/object.esm.js';

var _excluded = ["__variants", "__themeKey", "variant", "className", "alignItems", "autoFocus", "button", "children", "as", "ContainerComponent", "ContainerProps", "dense", "disabled", "disableGutters", "divider", "selected", "active", "data"];
/**
 * Uses an additional container component if `ListItemSecondaryAction` is the last child.
 */
var ListItemPrivate = /*#__PURE__*/React.forwardRef(function ListItemPrivate(props, ref) {
  var _props$__css, _props$sx, _props$__css2, _props$sx2;
  var __variants = props.__variants,
    _props$__themeKey = props.__themeKey,
    __themeKey = _props$__themeKey === void 0 ? LIST_THEME_KEY : _props$__themeKey,
    _props$variant = props.variant,
    variant = _props$variant === void 0 ? 'listItem' : _props$variant,
    className = props.className,
    _props$alignItems = props.alignItems,
    alignItems = _props$alignItems === void 0 ? 'center' : _props$alignItems,
    _props$autoFocus = props.autoFocus,
    autoFocus = _props$autoFocus === void 0 ? false : _props$autoFocus,
    _props$button = props.button,
    button = _props$button === void 0 ? false : _props$button,
    childrenProp = props.children,
    _props$as = props.as,
    component = _props$as === void 0 ? 'li' : _props$as,
    _props$ContainerCompo = props.ContainerComponent,
    ContainerComponent = _props$ContainerCompo === void 0 ? 'li' : _props$ContainerCompo,
    _props$ContainerProps = props.ContainerProps,
    ContainerProps = _props$ContainerProps === void 0 ? EMPTY_OBJECT : _props$ContainerProps,
    _props$dense = props.dense,
    dense = _props$dense === void 0 ? false : _props$dense,
    _props$disabled = props.disabled,
    disabled = _props$disabled === void 0 ? false : _props$disabled,
    _props$disableGutters = props.disableGutters,
    disableGutters = _props$disableGutters === void 0 ? false : _props$disableGutters,
    _props$divider = props.divider,
    divider = _props$divider === void 0 ? false : _props$divider,
    _props$selected = props.selected,
    selected = _props$selected === void 0 ? false : _props$selected,
    active = props.active,
    _props$data = props.data,
    data = _props$data === void 0 ? EMPTY_OBJECT : _props$data,
    other = _objectWithoutProperties(props, _excluded);
  // eslint-disable-next-line no-console
  // console.log(`---- render theme-ui.list-item ----`)
  var context = useListContext();
  var listItemRef = useRef(undefined);
  useIsomorphicEffect(function () {
    if (autoFocus) {
      if (listItemRef.current) {
        listItemRef.current.focus();
      } else if (process.env.NODE_ENV !== 'production') {
        console.error('Tui: unable to set focus to a ListItem whose component has not been rendered.');
      }
    }
  }, [autoFocus]);
  var children = React.Children.toArray(childrenProp);
  var hasSecondaryAction = children.length && isTuiElement(children[children.length - 1], ['ListItemSecondaryAction']);
  var handleOwnRef = useCallback(function (instance) {
    listItemRef.current = instance;
  }, []);
  var handleRef = useForkRef(handleOwnRef, ref);
  var combinePaddingX = _objectSpread2(_objectSpread2(_objectSpread2({}, getPaddingX((_props$__css = props.__css) !== null && _props$__css !== void 0 ? _props$__css : {})), getPaddingX(props)), getPaddingX((_props$sx = props.sx) !== null && _props$sx !== void 0 ? _props$sx : {}));
  var combinePaddingY = _objectSpread2(_objectSpread2(_objectSpread2({}, getPaddingY((_props$__css2 = props.__css) !== null && _props$__css2 !== void 0 ? _props$__css2 : {})), getPaddingY(props)), getPaddingY((_props$sx2 = props.sx) !== null && _props$sx2 !== void 0 ? _props$sx2 : {}));
  var componentProps = _objectSpread2(_objectSpread2({
    variant: __themeKey === LIST_THEME_KEY && !button // independent list component i.e. not part of other compound component
    ? context.variant ? "".concat(context.variant, ".listItem") : variant : variant,
    __themeKey: !button ? __themeKey : undefined,
    className: classNames({
      'csg-tui-list-item': true,
      'is-active': active,
      'is-selected': selected,
      'is-disabled': disabled,
      'is-dense': dense
    }, className),
    __css: _objectSpread2(_objectSpread2(_objectSpread2({
      display: 'flex',
      position: 'relative',
      justifyContent: 'flex-start',
      alignItems: alignItems,
      textDecoration: 'none',
      width: '100%',
      textAlign: 'left',
      userSelect: 'none'
    }, button && isEmptyObject(combinePaddingY) ? dense ? {
      emPaddingY: 'xs-3'
    } : {
      emPaddingY: 'xs-2'
    } : {}), !disableGutters && isEmptyObject(combinePaddingX) ? dense ? {
      emPaddingX: 'xxs'
    } : {
      emPaddingX: 'xs'
    } : {}), divider ? {
      borderBottom: 'neutral.light',
      backgroundClip: 'padding-box'
    } : {}),
    disabled: disabled,
    autoFocus: autoFocus,
    selected: selected
  }, isEmptyObject(data) ? {} : {
    data: data
  }), other);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  var Component = useMemo(function () {
    return button ? /*#__PURE__*/React.forwardRef(function ListItemButton(props, ref) {
      return /*#__PURE__*/React.createElement(ButtonPrivate, _extends({
        as: component,
        ref: ref
      }, props));
    }) : _typeof(component) === 'object' ? component : createComponent(component, 'csg-list-item', __variants);
  }, [__variants, button, component]);
  // eslint-disable-next-line no-console
  // console.log(`----- render list-item -----`)
  if (hasSecondaryAction) {
    // Use div by default.
    Component = !componentProps.as && !component ? 'div' : Component;

    // Avoid nesting of li > li.
    if (ContainerComponent === 'li') {
      if (component === 'li') {
        Component = Box;
      } else if (componentProps.as === 'li') {
        componentProps.as = 'div';
      }
    }
    return /*#__PURE__*/React.createElement(ListContextProvider, {
      dense: dense || context.dense || false,
      alignItems: alignItems,
      variant: !button ? variant : undefined
    }, /*#__PURE__*/React.createElement(ContainerComponent, _extends({
      relative: true,
      ref: handleRef
    }, ContainerProps), /*#__PURE__*/React.createElement(Component, _objectSpread2(_objectSpread2({}, componentProps), button ? {
      disabled: disabled
    } : {}), children), children.pop()));
  }
  return /*#__PURE__*/React.createElement(ListContextProvider, {
    dense: dense || context.dense || false,
    alignItems: alignItems,
    variant: !button ? variant : undefined
  }, /*#__PURE__*/React.createElement(Component, _extends({
    ref: handleRef
  }, _objectSpread2(_objectSpread2({}, componentProps), button ? {
    disabled: disabled
  } : {})), children));
});
var MemoList = /*#__PURE__*/React.memo(ListItemPrivate);
var ListItem = /*#__PURE__*/React.forwardRef(function ListItem(props, ref) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return /*#__PURE__*/React.createElement(MemoList, _extends({
    ref: ref
  }, props));
});

export default ListItem;
export { ListItemPrivate };
//# sourceMappingURL=list-item.esm.js.map
